import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'astroturf/react';
import AvatarBase from './shared/Avatar';
import { Body } from 'js/design_system/Body';
import { Paper, ResourceType, Image } from 'js/social/shared/types';
import CreateSocialItem from 'js/social/shared/CreateSocialItem/CreateSocialItem';
import useCurrentUserAsAuthor from 'js/social/shared/CreateSocialItem/useCurrentUserAsAuthor';
import WhoCanSeePost from 'js/social/shared/CreateSocialItem/WhoCanSeePost';
import useShowToggle from 'js/social/shared/useShowToggle';
import { createPost } from 'js/social/api/post_api';
import { FeedContext } from 'js/social/contexts/feed';
import {
  getImpressionPage,
  getSocialImpressionSource,
} from 'js/social/ImpressionHelper';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { uploadComicImage } from 'js/ai_work_comic/api';
import { LoadingBanner } from 'js/social/shared/attachments/useAttachment';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';

const Container = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;

  @include medium-up {
    border: 1px solid #ebebee;
    border-radius: 6px;
  }
`;

const Avatar = styled(AvatarBase)`
  flex-shrink: 0;
  --size: 48px;

  @include for-tablet-up {
    --size: 60px;
  }
`;

const InputContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

// this looks like an input but is actually a button,
// since clicking on it doesn't allow the user to type,
// it just opens the CreatePost modal.
const PostInputButton = styled(Body)`
  width: 100%;
  padding: 9px 12px;
  text-align: left;
  color: $ds-gray-dark;
  background-color: $ds-white;
  border-radius: 4px;
  border: 1px solid #b1b1ba;
  cursor: text;
`;

interface Props {
  /** Allow this component to be controlled */
  open?: boolean;
  modalOnly?: boolean;
  fullName: string;
  photoUrl: string;
  profileUrl: string;
  className?: string;
  onClose?: () => void;
  initialPaper?: Paper;
  initialContent?: string;
  initialImageUrl?: string;
  initialImage?: Image;
  comicWorkId?: number;
}

export const PostPrompt: React.FC<Props> = ({
  open,
  modalOnly = false,
  fullName,
  photoUrl,
  profileUrl,
  className,
  onClose = () => null,
  initialPaper,
  initialContent,
  initialImageUrl,
  initialImage,
  comicWorkId,
}) => {
  const {
    feedConfig: { page },
    newsActions: { addFeedNews },
  } = useContext(FeedContext);
  const author = useCurrentUserAsAuthor();

  // State for comic photo loading
  const [comicPhoto, setComicPhoto] = useState<Image | null>(null);
  const [comicPhotoLoading, setComicPhotoLoading] = useState(false);

  // Load comic photo when comicWorkId is provided
  useEffect(() => {
    if (comicWorkId && !comicPhoto && !comicPhotoLoading) {
      setComicPhotoLoading(true);

      uploadComicImage(comicWorkId)
        .then((result) => {
          setComicPhoto({
            id: result.photo.id,
            url: result.photo.url,
            width: null,
            height: null,
            size: null,
          });
          setComicPhotoLoading(false);
        })
        .catch((error) => {
          console.error('Error loading comic photo:', error);
          setComicPhotoLoading(false);
        });
    }
  }, [comicWorkId, comicPhoto, comicPhotoLoading]);

  const {
    isShown: showCreatePostModal,
    show: openCreatePostModal,
    hide: closeCreatePostModal,
  } = useShowToggle();

  const handleClose = useCallback(() => {
    onClose();
    if (open === undefined) {
      closeCreatePostModal();
    }
  }, [open, onClose, closeCreatePostModal]);

  // Calculate the initial image to pass to CreateSocialItem
  const resolvedInitialImage =
    initialImage ||
    comicPhoto ||
    (initialImageUrl
      ? {
          id: 'comic-image',
          url: initialImageUrl,
          width: null,
          height: null,
          size: null,
        }
      : undefined);

  return (
    <>
      {!modalOnly && (
        <ImpressionWatcher
          source={getSocialImpressionSource(getImpressionPage(page), 'prompt')}
        >
          <Container className={className}>
            <Avatar
              fullName={fullName}
              photoUrl={photoUrl}
              profileUrl={profileUrl}
            />
            <InputContainer>
              <PostInputButton
                size="sm"
                aria-label="Create post"
                onClick={openCreatePostModal}
              >
                Share a thought with other scholars
              </PostInputButton>
            </InputContainer>
          </Container>
        </ImpressionWatcher>
      )}

      {(open || showCreatePostModal) && (
        <CreateSocialItem
          id="post:new"
          type={ResourceType.Post}
          author={author}
          heading="Create post"
          placeholder="Share a thought with other scholars"
          cta="Post"
          footer={<WhoCanSeePost />}
          onClose={handleClose}
          onSubmit={async (payload) => {
            const { post, user } = await createPost(payload);
            addFeedNews(post, [user]);
          }}
          initialPaper={initialPaper}
          initialContent={initialContent}
          initialImage={resolvedInitialImage}
        >
          {comicPhotoLoading && !comicPhoto && (
            <LoadingBanner icon={<Spinner />}>
              Your comic is loading, it will be viewable shortly.
            </LoadingBanner>
          )}
        </CreateSocialItem>
      )}
    </>
  );
};
